/**
 * Phase 5 operational portal, reporting and dashboard types.
 */

import type { OperationalDashboardExtras } from './admin-read-models';
import type { CustomerDashboardMode, CustomerOnboardingStepView } from './customer-journey';
import type { RepaymentScheduleBillingView } from './phase4';

export type { CustomerOnboardingStepView };

export interface DashboardMetricCard {
  readonly key: string;
  readonly label: string;
  readonly value: number | string;
  readonly filterPath?: string;
  readonly filterQuery?: Record<string, string>;
}

/**
 * Staff operational dashboard envelope.
 *
 * `cards` remain the compatibility surface for the current portal.
 * Additive read-model sections are optional and scope-specific.
 */
export interface OperationalDashboardView extends OperationalDashboardExtras {
  readonly generatedAt: string;
  readonly cards: readonly DashboardMetricCard[];
}

export interface CustomerActionCard {
  readonly code: string;
  readonly title: string;
  readonly description: string;
  readonly actionPath: string;
  readonly priority: 'high' | 'medium' | 'low';
}

export interface CustomerRecentPaymentView {
  readonly id: string;
  readonly internalReference: string;
  readonly amount: string;
  readonly postedAt: string;
  readonly status: string;
}

export interface CustomerJourneyStageView {
  readonly key: string;
  readonly label: string;
  readonly state: 'complete' | 'current' | 'upcoming';
}

export interface CustomerDashboardView {
  /** Server-resolved lifecycle mode. Optional only for backward-compatible cached payloads. */
  readonly mode?: CustomerDashboardMode;
  /** Compatibility field for the legacy dashboard shell. */
  readonly profileComplete: boolean;
  readonly hasCustomerProfile: boolean;
  readonly customerFirstName: string | null;
  readonly onboardingComplete: boolean;
  readonly onboardingStage: string | null;
  readonly onboardingSteps: readonly CustomerOnboardingStepView[];
  readonly relevantSubscriptionId: string | null;
  readonly applicationStatus: string | null;
  readonly rejectionReason: string | null;
  readonly changeRequestReason: string | null;
  readonly planFullyPaid: boolean;
  readonly nextScheduleStatus: string | null;
  readonly contractReference: string | null;
  readonly productName: string | null;
  readonly contractAmount: string | null;
  readonly depositAmount: string | null;
  readonly financedAmount: string | null;
  readonly tenureMonths: number | null;
  readonly mandateStatus: string | null;
  readonly nextDueDate: string | null;
  readonly nextInstalmentAmount: string | null;
  readonly nextScheduleId: string | null;
  readonly activeAuthorizationSessionId: string | null;
  readonly overdueAmount: string | null;
  readonly totalPaidAmount: string | null;
  readonly outstandingBalance: string | null;
  readonly paidInstalmentCount: number | null;
  readonly totalInstalmentCount: number | null;
  readonly latestPaymentStatus: string | null;
  readonly latestReceiptId: string | null;
  readonly currency: string;
  readonly recentPayments: readonly CustomerRecentPaymentView[];
  readonly journeyStages: readonly CustomerJourneyStageView[];
  readonly requiredActions: readonly CustomerActionCard[];
}

export interface TimelineEventView {
  readonly id: string;
  readonly occurredAt: string;
  readonly category: string;
  readonly label: string;
  readonly description: string | null;
  readonly entityType: string | null;
  readonly entityId: string | null;
}

export interface OverdueSummaryView {
  readonly overdueCount: number;
  readonly awaitingAuthorizationCount: number;
  readonly totalOverdueAmount: string;
  readonly items: readonly RepaymentScheduleBillingView[];
}

export interface ReportDateRangeQuery {
  readonly from?: string;
  readonly to?: string;
  readonly page?: number;
  readonly pageSize?: number;
}

export interface CustomerReportSummary {
  readonly registrations: number;
  readonly incompleteOnboarding: number;
  readonly applicationsByStatus: Readonly<Record<string, number>>;
}

export interface BillingReportSummary {
  readonly dueCount: number;
  readonly dueAmount: string;
  readonly collectedCount: number;
  readonly collectedAmount: string;
  readonly collectionRate: string;
  readonly awaitingAuthorization: number;
  readonly processing: number;
  readonly failed: number;
  readonly overdueCount: number;
  readonly overdueAmount: string;
}

export interface MandateReportSummary {
  readonly created: number;
  readonly pendingActivation: number;
  readonly active: number;
  readonly failed: number;
  readonly expired: number;
  readonly cancellationRequests: number;
}

export interface NotificationReportSummary {
  readonly queued: number;
  readonly sent: number;
  readonly failed: number;
  readonly retrying: number;
  readonly processing: number;
  readonly staleProcessing: number;
  readonly oldestUnsentAt: string | null;
}

export interface NotificationOutboxItemView {
  readonly id: string;
  readonly channel: string;
  readonly templateCode: string;
  readonly recipient: string;
  readonly subject: string;
  readonly status: string;
  readonly attemptCount: number;
  readonly nextAttemptAt: string | null;
  readonly sentAt: string | null;
  readonly failureCode: string | null;
  readonly failureMessage: string | null;
  readonly relatedEntityType: string | null;
  readonly relatedEntityId: string | null;
  readonly idempotencyKey: string;
  readonly createdAt: string;
}

export interface SystemHealthView {
  readonly apiLive: boolean;
  readonly databaseReady: boolean;
  readonly paymentProvider: string;
  readonly mockProviderWarning: boolean;
  readonly environment: string;
  readonly applicationVersion: string | null;
  readonly notificationBacklog: number;
  readonly pendingMandatePolls: number;
  readonly pendingPaymentReconciliation: number;
  readonly failedNotifications: number;
}

export const REPORT_TYPES = [
  'customers',
  'applications',
  'contracts',
  'mandates',
  'billing',
  'payments',
  'overdue',
  'notifications',
  'audit',
] as const;

export type ReportType = (typeof REPORT_TYPES)[number];
